import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Wand2, RefreshCw, Download, Crown, Save, Shuffle, ShieldCheck, AlertTriangle, GitCompare, Check } from "lucide-react";
import { NeonButton } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COST = 35;

const download = (dataUrl, name) => {
  const a = document.createElement("a"); a.href = dataUrl; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
};

export default function IdentityPreview({ item, onClose, onMemoryUpdated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenIdx, setRegenIdx] = useState(null);
  const [master, setMaster] = useState(item.master);
  const [compare, setCompare] = useState(null);
  const { refreshUser } = useAuth();

  useEffect(() => {
    api.get(`/vault/${item.id}`).then(({ data }) => { setPreview(data.preview || null); setMaster(data.master); }).catch(() => {});
  }, [item.id]);

  const runPreview = async (isVariation) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/vault/${item.id}/preview`);
      setPreview(data.preview);
      await refreshUser();
      const s = data.preview.score;
      if (s.status === "DRIFT") toast.warning(`Consistency ${s.Overall}% — below lock threshold. ${s.warning}`);
      else toast.success(`${isVariation ? "Variations generated" : "Profile previewed"} · ${s.Overall}% LOCKED`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const regen = async (i) => {
    setRegenIdx(i);
    try {
      const { data } = await api.post(`/vault/${item.id}/preview/regenerate`, { shot_index: i });
      setPreview(data.preview);
      await refreshUser();
      toast[data.status === "failed" ? "warning" : "success"](data.status === "failed" ? "Shot failed — Credit Shield refunded" : "Shot regenerated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRegenIdx(null); }
  };

  const saveMaster = async () => {
    try { await api.patch(`/vault/${item.id}`, { master: true }); setMaster(true); toast.success("Saved as Master Character"); onMemoryUpdated && onMemoryUpdated(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const updateMemory = async (image) => {
    try { await api.patch(`/vault/${item.id}`, { image }); toast.success("Character memory updated — future generations use this reference"); onMemoryUpdated && onMemoryUpdated(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCompare = async (i) => {
    try { const { data } = await api.post(`/vault/${item.id}/compare`, { shot_index: i }); setCompare({ ...data, index: i }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const acceptGeneration = async () => {
    if (!compare) return;
    await updateMemory(compare.generated_image);
    setCompare(null);
  };
  const regenerateFrame = async () => {
    if (!compare) return;
    const i = compare.index;
    setCompare(null);
    await regen(i);
  };

  const score = preview?.score;
  const locked = score?.status === "LOCKED";
  const RESERVED = ["Overall", "status", "threshold", "warning", "note", "per_shot", "flags", "biometric"];
  const metricKeys = score ? Object.keys(score).filter((k) => typeof score[k] === "number" && !RESERVED.includes(k)) : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-testid="identity-preview">
      <div className="glass clip-corner border border-white/10 max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6" style={{ borderTop: "1px solid rgba(255,0,127,0.6)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="font-display font-bold text-2xl flex items-center gap-2">Preview From Profile™ {master && <Crown size={16} className="text-neon-yellow" />}</h2>
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-neon-pink mt-1">{item.name} · identity consistency test</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-neon-pink transition-colors" data-testid="close-preview"><X size={20} /></button>
        </div>

        {!preview && !loading && (
          <div className="text-center py-14">
            <ShieldCheck size={40} className="text-neon-pink mx-auto mb-4" />
            <p className="font-mono-tech text-white/60 text-sm max-w-md mx-auto">Instantly test whether {item.name} stays consistent across poses, lighting & backgrounds — before building a full campaign.</p>
            <div className="mt-8 flex justify-center">
              <NeonButton accent="pink" onClick={() => runPreview(false)} data-testid="run-preview-btn"><Sparkles size={15} /> Preview From Profile™ ({COST} credits)</NeonButton>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-20 font-mono-tech text-neon-pink text-sm">
            <div className="w-12 h-12 border-2 border-neon-pink border-t-transparent rounded-full animate-spin mx-auto mb-5" />
            GENERATING CONSISTENCY PREVIEW — 7 SHOTS…
          </div>
        )}

        {preview && !loading && (
          <>
            {/* Consistency Score */}
            <div className="grid md:grid-cols-[1fr_260px] gap-6 mt-5 mb-6" data-testid="consistency-score">
              <div className="border border-white/10 p-5 space-y-3">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50">{score.biometric ? "Biometric Identity Analysis" : "Consistency Analysis"}</span>
                {metricKeys.map((k) => {
                  const good = score[k] >= (score.threshold || 90);
                  return (
                    <div key={k} data-testid={`metric-${k}`}>
                      <div className="flex justify-between font-mono-tech text-[11px]"><span className="text-white/60">{k}</span><span style={{ color: good ? "#39ff14" : "#ff007f" }}>{score[k]}%</span></div>
                      <div className="h-1.5 bg-white/10 mt-1"><div className="h-full" style={{ width: `${score[k]}%`, background: good ? "#39ff14" : "#ff007f", boxShadow: `0 0 8px ${good ? "#39ff14" : "#ff007f"}` }} /></div>
                    </div>
                  );
                })}
                <p className="font-mono-tech text-[9px] text-white/25 pt-1">{score.note}</p>
              </div>
              <div className="border p-5 flex flex-col items-center justify-center text-center" style={{ borderColor: locked ? "rgba(57,255,20,0.4)" : "rgba(255,0,127,0.5)" }} data-testid="overall-score">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Overall Consistency</span>
                <div className="font-display font-black text-5xl mt-2" style={{ color: locked ? "#39ff14" : "#ff007f" }}>{score.Overall}%</div>
                <div className="font-mono-tech text-xs uppercase tracking-[0.3em] mt-1" style={{ color: locked ? "#39ff14" : "#ff007f" }}>{score.status}</div>
              </div>
            </div>

            {score.warning && (
              <div className="flex items-start gap-3 border border-neon-pink/40 bg-neon-pink/5 p-4 mb-6 font-mono-tech text-[11px] text-neon-pink" data-testid="drift-warning">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {score.warning}
              </div>
            )}
            {score.flags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="drift-flags">
                {score.flags.map((f) => (
                  <span key={f} className="font-mono-tech text-[10px] uppercase tracking-wide border border-neon-pink/40 text-neon-pink px-2 py-1">{f}</span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mb-6">
              <NeonButton accent="yellow" onClick={saveMaster} disabled={master} data-testid="save-master-btn"><Crown size={13} /> {master ? "Master Character" : "Save as Master"}</NeonButton>
              <NeonButton accent="turquoise" onClick={() => runPreview(true)} data-testid="variations-btn"><Shuffle size={13} /> Generate Variations</NeonButton>
            </div>

            {/* Shots */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {preview.shots.map((s, i) => (
                <div key={i} className="border border-white/10" data-testid={`preview-shot-${i}`}>
                  <div className="aspect-square bg-black/40 overflow-hidden relative group">
                    {s.image ? (
                      <>
                        <img src={s.image} alt={s.shot} className="w-full h-full object-cover" />
                        <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openCompare(i)} className="bg-black/70 border border-white/20 p-1.5 text-neon-pink" title="Compare to Master" data-testid={`compare-shot-${i}`}><GitCompare size={13} /></button>
                          <button onClick={() => regen(i)} className="bg-black/70 border border-white/20 p-1.5 text-neon-turquoise" title="Regenerate" data-testid={`preview-regen-${i}`}><RefreshCw size={13} /></button>
                          <button onClick={() => updateMemory(s.image)} className="bg-black/70 border border-white/20 p-1.5 text-neon-yellow" title="Set as character memory" data-testid={`set-memory-${i}`}><Save size={13} /></button>
                          <button onClick={() => download(s.image, `${item.name}-${s.shot}.png`)} className="bg-black/70 border border-white/20 p-1.5 text-white/80" title="Download"><Download size={13} /></button>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center font-mono-tech text-[9px] text-neon-pink px-2">
                        FAILED · REFUNDED
                        <button onClick={() => regen(i)} className="border border-neon-turquoise/50 text-neon-turquoise px-2 py-1 uppercase" data-testid={`preview-regen-${i}`}>Retry</button>
                      </div>
                    )}
                    {regenIdx === i && <div className="absolute inset-0 bg-black/70 flex items-center justify-center"><div className="w-7 h-7 border-2 border-neon-turquoise border-t-transparent rounded-full animate-spin" /></div>}
                  </div>
                  <div className="p-2 font-mono-tech text-[10px] text-white/50">{s.shot}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {compare && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setCompare(null); }} data-testid="compare-panel">
          <div className="glass clip-corner border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" style={{ borderTop: "1px solid rgba(255,0,127,0.6)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-lg">Side-by-Side · {compare.shot}</h3>
              <button onClick={() => setCompare(null)} className="text-white/50 hover:text-neon-pink transition-colors"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neon-yellow mb-1 flex items-center gap-1"><Crown size={11} /> Master Character</div>
                <div className="aspect-square border border-neon-yellow/40 overflow-hidden">{compare.master_image ? <img src={compare.master_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-mono-tech text-xs">no master</div>}</div>
              </div>
              <div>
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neon-turquoise mb-1">Generated Character</div>
                <div className="aspect-square border border-neon-turquoise/40 overflow-hidden"><img src={compare.generated_image} alt="" className="w-full h-full object-cover" /></div>
              </div>
            </div>
            <div className="mt-5 space-y-2.5">
              {Object.entries(compare.matches).map(([k, v]) => {
                const low = v < compare.threshold;
                return (
                  <div key={k} data-testid={`match-${k}`}>
                    <div className="flex justify-between font-mono-tech text-[11px]">
                      <span className={low ? "text-neon-pink" : "text-white/60"}>{k}{low && " · changed"}</span>
                      <span style={{ color: low ? "#ff007f" : "#39ff14" }}>{v}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 mt-1"><div className="h-full" style={{ width: `${v}%`, background: low ? "#ff007f" : "#39ff14", boxShadow: `0 0 8px ${low ? "#ff007f" : "#39ff14"}` }} /></div>
                  </div>
                );
              })}
            </div>
            {compare.changed.length > 0 && (
              <div className="flex items-start gap-2 border border-neon-pink/40 bg-neon-pink/5 p-3 mt-4 font-mono-tech text-[11px] text-neon-pink">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" /> Changes detected in: {compare.changed.join(", ")}. Regenerate to tighten the match.
              </div>
            )}
            <div className="flex gap-3 mt-6">
              <NeonButton accent="green" onClick={acceptGeneration} data-testid="accept-generation-btn"><Check size={13} /> Accept Generation</NeonButton>
              <NeonButton accent="pink" onClick={regenerateFrame} data-testid="regenerate-frame-btn"><RefreshCw size={13} /> Regenerate Frame</NeonButton>
            </div>
            <p className="font-mono-tech text-[9px] text-white/25 mt-3">{compare.note}</p>
          </div>
        </div>
      )}
    </div>
  );
}
